/**
 * Calendar days in the browser — the counterpart of `backend/src/utils/calendarDate.ts`.
 *
 * ### The distinction this module exists to enforce
 *
 * A **day** and an **instant** are different things, and `Date` is only the second one. Every date
 * defect found on 15 Aug came from moving between them with the wrong conversion, so this module
 * names the three operations separately and refuses to let one stand in for another:
 *
 * | You have | You want | Use |
 * |---|---|---|
 * | A day the SERVER sent (`'2026-09-30'`, or a `Date` the model hydrated from one) | its day | `toCalendarDay` — reads **UTC** parts, because that is where the API's midnight is |
 * | A `Date` the BROWSER built (a picker's `new Date(2026, 8, 30)`, `new Date()`) | its day | `localCalendarDay` — reads **local** parts, because that is where the user's midnight is |
 * | Nothing — you want the current day | today | `todayCalendarDay(zone)` |
 *
 * **`toISOString().split('T')[0]` is never the right answer for either.** On a browser-built date
 * it re-reads local midnight in UTC, so west of UTC it prints yesterday and east of it, for the
 * first hours of the day, tomorrow. That is how "Today" in the due-date picker wrote the 16th
 * while the user was looking at the 15th, and how a worklog defaulted onto the wrong day. It was
 * in eleven places; `grep "toISOString().split"` is how you find the twelfth.
 *
 * The two directions are deliberately NOT symmetrical, and that asymmetry is the whole point: a
 * stored day arrives at UTC midnight and a picked day is built at local midnight, so reading both
 * the same way is guaranteed to be wrong for one of them.
 */

/** `2026-09-30` — a day, with no zone and no time. */
export type CalendarDay = string;

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

/**
 * The day a value from the SERVER names, as `YYYY-MM-DD`.
 *
 * A bare `'2026-09-30'` passes through untouched. Anything else is read in **UTC**, because a
 * calendar date from the API is `'2026-09-30'` and `new Date` puts that at UTC midnight — the
 * model hydrates `{ type: 'date' }` fields into `Date`, so the same field is a string on the
 * response and a `Date` after a reload, and both must print the 30th.
 *
 * Do not pass a browser-built date here. Use {@link localCalendarDay} for those.
 */
export function toCalendarDay(value: Date | string | null | undefined): CalendarDay | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const head = value.slice(0, 10);
    if (DAY_ONLY.test(head)) return head;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * The day a BROWSER-BUILT `Date` names, as `YYYY-MM-DD`, read from its **local** parts.
 *
 * A calendar picker hands you local midnight on the day the user clicked. This is the only
 * conversion that returns that day in every zone.
 */
export function localCalendarDay(value: Date): CalendarDay | null {
  if (Number.isNaN(value.getTime())) return null;
  return `${pad(value.getFullYear(), 4)}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

/**
 * Today, as `YYYY-MM-DD`, in `timeZone` — or in the browser's own zone when none is given.
 *
 * A workspace can configure its timezone, and when it has, "today" is the workspace's today
 * rather than the reader's. Callers inside the app should reach this through `workspaceToday()`
 * in `workspaceDateTime.ts`, which supplies the resolved zone; this signature stays pure so it
 * can be tested at any hour, in any zone, without a store.
 */
export function todayCalendarDay(timeZone?: string | null, now: Date = new Date()): CalendarDay {
  if (!timeZone) return localCalendarDay(now)!;
  try {
    /* 'en-CA' is `YYYY-MM-DD` natively, so there are no parts to reassemble and mis-order. */
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return localCalendarDay(now)!;
  }
}

/** `n` days after `day` (negative for before), staying on the calendar throughout. */
export function addCalendarDays(day: CalendarDay, n: number): CalendarDay {
  const [y, m, d] = day.split('-').map(Number);
  /* UTC arithmetic: no zone has a DST transition in it, so no day is ever skipped or repeated. */
  const t = new Date(Date.UTC(y!, m! - 1, d! + n));
  return toCalendarDay(t)!;
}

/**
 * Whole days from `from` to `to` — positive when `to` is later.
 *
 * Both sides are days, so the answer is exact: no hours, no DST, no rounding. `Math.round` over
 * two instants is what produced "Overdue 1 day" on the day something was actually due.
 */
export function calendarDayDiff(from: CalendarDay, to: CalendarDay): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * A day, formatted for reading — always in UTC, because the day is the same day everywhere.
 *
 * Use this instead of `toLocaleDateString` on a date field. Formatting a day in a workspace zone
 * west of UTC renders the one before it, which is the defect the US-clock soak caught on the
 * issue rail: the database held the 30th and the panel said 29 Sept.
 */
export function formatCalendarDay(
  value: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' },
  locale?: string
): string | null {
  const day = toCalendarDay(value);
  if (!day) return null;
  return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...options }).format(
    new Date(`${day}T00:00:00Z`)
  );
}
